import { useQuery } from '@tanstack/react-query';
import type { FundingDashboard, FundingDashboardRangeEnum } from '@/lib/api-admin/types';
import { apiClient } from '@/lib/apiClient';

export interface FundingDashboardParams {
  range: FundingDashboardRangeEnum;
  /** range=CUSTOM 일 때만 사용 (UTC ISO-8601). */
  from?: string;
  to?: string;
  /** topCampaigns 길이 (default 5, max 20). */
  topN?: number;
  vendorId?: string | null;
}

// ADR-031 §1.1: 30 초 polling 권장 + SSE 보조 채널.
// ★SSE(/funding-campaigns/events)는 본 백오피스의 Bearer-token 인증과 비호환:
//   EventSource 는 Authorization 헤더를 못 보내고, 이벤트 endpoint 는 token query
//   fallback 도 없어(query=lastEventId|vendorId 뿐) 401 로 무한 재시도한다. 따라서
//   라이브 갱신은 polling(30s)을 권위 채널로 쓴다. BE 가 events endpoint 에 token
//   query 인증을 추가하면 SSE 보조 채널을 재도입할 수 있다(아래 useEventStream 헬퍼 재사용).
export const POLL_INTERVAL_MS = 30_000;

export const cleanQuery = (params: FundingDashboardParams) => {
  const q: {
    range: FundingDashboardRangeEnum;
    from?: string;
    to?: string;
    topN?: number;
    vendorId?: string;
  } = { range: params.range };
  if (params.range === 'CUSTOM') {
    if (params.from) q.from = params.from;
    if (params.to) q.to = params.to;
  }
  if (params.topN != null) q.topN = params.topN;
  if (params.vendorId) q.vendorId = params.vendorId;
  return q;
};

export const fundingDashboardQueryKey = (params: FundingDashboardParams) =>
  ['admin', 'funding-dashboard', params] as const;

export const useFundingDashboardQuery = (params: FundingDashboardParams) =>
  useQuery<FundingDashboard>({
    queryKey: fundingDashboardQueryKey(params),
    refetchInterval: POLL_INTERVAL_MS,
    // 탭 비활성/재포커스 시에도 신선도 유지(모니터링 화면 특성).
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const [data] = await apiClient.request('get', '/admin/v1/funding-campaigns/dashboard', {
        params: { query: cleanQuery(params) },
      });
      return data as FundingDashboard;
    },
  });
